import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import SearchRounded from '@mui/icons-material/SearchRounded'
import { Autocomplete, Box, Chip, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { borderRadiusDefault } from '../../../../themes/theme'
import { clickableProps } from '../../helpers/interactive'
import { relativePct } from '../../helpers/relativeMetric'
import { linkTypographyBoldSx, linkTypographySx } from '../../helpers/styles'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { TagCount, TagRef } from '../../types'
import { RelativeMetricBar } from '../RelativeMetricBar/RelativeMetricBar'
import { SectionCard } from '../SectionCard/SectionCard'

const COLLAPSED_TAG_COUNT = 10

const tagRowId = (row: TagRow) => row.tag.id

interface TagRow {
  tag: TagRef
  count: number
  /** Share of creative ads featuring this tag, expressed as a percentage (0-100). */
  distribution: number
}

interface GameCreativeTagsTableProps {
  /** Tag aggregates derived from the game's creatives in the current scope. */
  tags: TagCount[]
  /** Total number of creative ads currently in scope, used as the distribution denominator. */
  totalAds: number
  /**
   * Show every tag and hide the expand/collapse toggle. Used by views where
   * the table is the primary content (e.g. the dedicated "Creative Tags" tab
   * on the UA Insights game page) and pagination/collapsing would just hide
   * data. Inline embedded contexts (e.g. the Motivation Details dialog) keep
   * the default collapsed-with-toggle behaviour.
   */
  showAll?: boolean
}

/**
 * Lists every creative tag attached to the game's ads, with the group it
 * belongs to, how many ads carry it, and a share-of-ads bar visually mirroring
 * the "Ad Performance" column in the Creative Ads table — the leader fills the
 * bar and weaker tags scale proportionally, while the label always shows the
 * absolute distribution percentage.
 */
export const GameCreativeTagsTable: FC<GameCreativeTagsTableProps> = ({ tags, totalAds, showAll = false }) => {
  const { t } = useTranslation('ua-insights')
  const { openTag, openDimensionTags } = useUAInsightsActions()
  const { tagLabel, dimensionLabel } = useUATaxonomy()
  const [expanded, setExpanded] = useState(false)
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])

  const allRows: TagRow[] = useMemo(
    () =>
      tags.map(({ tag, count }) => ({
        tag,
        count,
        distribution: totalAds > 0 ? (count / totalAds) * 100 : 0,
      })),
    [tags, totalAds]
  )

  // Distinct group (dimension) options powering the filter autocomplete; sorted
  // alphabetically by display label so the dropdown is predictable to scan.
  const groupOptions = useMemo(() => {
    const seen = new Set<string>()
    const groups: string[] = []
    for (const { tag } of tags) {
      if (!seen.has(tag.dimensionId)) {
        seen.add(tag.dimensionId)
        groups.push(tag.dimensionId)
      }
    }
    return groups.sort((a, b) => dimensionLabel(a).localeCompare(dimensionLabel(b)))
  }, [tags, dimensionLabel])

  const rows = useMemo(() => {
    if (selectedGroups.length === 0) return allRows
    const groupSet = new Set(selectedGroups)
    return allRows.filter((row) => groupSet.has(row.tag.dimensionId))
  }, [allRows, selectedGroups])

  // Scale the bar relative to the strongest tag in the current view so the
  // leader always fills the track and weaker tags read as proportional.
  const maxDistribution = useMemo(() => rows.reduce((max, row) => Math.max(max, row.distribution), 0), [rows])

  const defaultColumns: GRTableColumn<TagRow, undefined>[] = useMemo(
    () => [
      {
        labelAccessor: t('col_tag_name'),
        accessor: ({ row }) => (
          <GRTooltip content={tagLabel(row.tag)} styles={{ display: 'block' }} tooltipProps={{ enterDelay: 700, enterNextDelay: 300 }}>
            <Typography variant="body2" sx={linkTypographyBoldSx} {...clickableProps(() => openTag(row.tag.id))}>
              {tagLabel(row.tag)}
            </Typography>
          </GRTooltip>
        ),
        sortAccessor: ({ row }) => tagLabel(row.tag),
        sortable: true,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_group'),
        accessor: ({ row }) =>
          row.tag.dimensionId ? (
            <GRTooltip content={dimensionLabel(row.tag.dimensionId)} styles={{ display: 'block' }} tooltipProps={{ enterDelay: 700, enterNextDelay: 300 }}>
              <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => openDimensionTags(row.tag.dimensionId))}>
                {dimensionLabel(row.tag.dimensionId)}
              </Typography>
            </GRTooltip>
          ) : (
            ''
          ),
        sortAccessor: ({ row }) => (row.tag.dimensionId ? dimensionLabel(row.tag.dimensionId) : ''),
        sortable: true,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_count'),
        accessor: 'count',
        sortAccessor: 'count',
        sortable: true,
        width: 100,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold' } },
      },
      {
        labelAccessor: t('col_distribution'),
        accessor: ({ row }) => {
          // The bar is scaled against the most-used tag, but the readout shows
          // the tag's own absolute distribution rather than the relative figure.
          return <RelativeMetricBar pct={relativePct(row.distribution, maxDistribution)} label={`${row.distribution.toFixed(0)}%`} sx={{ minWidth: 140 }} />
        },
        sortAccessor: 'distribution',
        sortable: true,
        sortOrder: SortOrder.DESC,
        width: 300,
        cellProps: { align: 'center', sx: { width: 300, minWidth: 300, maxWidth: 300 } },
        headerCellProps: { align: 'center', sx: { fontWeight: 'bold', width: 300, minWidth: 300, maxWidth: 300 } },
      },
    ],
    [t, openTag, openDimensionTags, maxDistribution, tagLabel, dimensionLabel]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  const canExpand = !showAll && rows.length > COLLAPSED_TAG_COUNT
  const toggleLabel = expanded ? t('common:show_less') : t('common:show_more')
  const groupFilterLabel = t('filter_by_creative_tag_group')

  return (
    <SectionCard
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexShrink: 0 }}>
            {t('game_creative_tags')}
          </Typography>
          {groupOptions.length > 0 && (
            <Autocomplete
              multiple
              size="small"
              options={groupOptions}
              value={selectedGroups}
              onChange={(_event, value) => setSelectedGroups(value)}
              getOptionLabel={(option) => dimensionLabel(option)}
              isOptionEqualToValue={(option, value) => option === value}
              sx={{ flex: 1, minWidth: 240 }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return <Chip key={key} {...tagProps} size="small" label={dimensionLabel(option)} />
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder={selectedGroups.length === 0 ? groupFilterLabel : undefined}
                  aria-label={groupFilterLabel}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ ml: 0.5, mr: 0.5 }}>
                          <SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: borderRadiusDefault,
                      backgroundColor: 'background.paper',
                      paddingLeft: 1.5,
                      // Lock the field to a fixed 40px height so it doesn't grow
                      // when chips are added. We zero out the inner padding and
                      // tag margin and rely on `alignItems: center` to vertically
                      // centre both the input text and selected chips.
                      paddingTop: 0,
                      paddingBottom: 0,
                      minHeight: 40,
                      height: 40,
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      overflow: 'hidden',
                      transition: 'box-shadow 0.2s ease',
                      '&:hover': { boxShadow: 2 },
                      '&.Mui-focused': { boxShadow: 3 },
                    },
                    '& .MuiAutocomplete-input': {
                      padding: '0 4px 0 0 !important',
                      minWidth: '30px !important',
                    },
                    '& .MuiAutocomplete-tag': {
                      my: 0,
                    },
                  }}
                />
              )}
            />
          )}
        </Box>
      }
      disableBodyPadding
      headerAction={
        showAll ? undefined : (
          <Tooltip title={toggleLabel} disableHoverListener={!canExpand} disableFocusListener={!canExpand} disableTouchListener={!canExpand}>
            <span>
              <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} aria-label={toggleLabel} disabled={!canExpand}>
                {expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
              </IconButton>
            </span>
          </Tooltip>
        )
      }
    >
      <GRTable
        rows={rows}
        columns={columns}
        onColumnsUpdated={setColumns}
        maxRows={showAll || expanded ? undefined : COLLAPSED_TAG_COUNT}
        striped
        hoverable
        rowIdKey={tagRowId}
        noRowsLabel={t('no_tags')}
      />
    </SectionCard>
  )
}
